import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
    BrowserRouter as Router,
    Routes,
    Route,
    Link,
    Navigate,
    useLocation,
} from 'react-router-dom';

import './style.css';
import Boosts from './frames/Boosts';
import Appeals from './frames/Appeals';
import Birthdays from './frames/Birthdays';
import Settings from './frames/Settings';

export const API_URL = process.env.REACT_APP_API_URL || "https://api.panel.mhadiscord.com";

axios.defaults.withCredentials = true;

console.log('[PANEL] Initializing with API_URL:', API_URL);

interface User {
    id: string;
    username: string;
    discriminator: string;
    avatar: string;
}

interface Stats {
    totalRecords: number;
    currentBoosters: number;
    guilds: number;
}

interface BoostRecord {
    uniqueID: string;
    discordID: string;
    discordUsername: string;
    guildID: string;
    guildName: string;
    premiumStart: string | null;
    premiumSince: string | null;
    currentlyBoosting: boolean;
    boostCount: number;
    lastUpdated: string;
    entryCreated: string;
}

const NavLink: React.FC<{ to: string; children: React.ReactNode }> = ({ to, children }) => {
    const location = useLocation();
    const isActive = location.pathname === to || (to !== '/' && location.pathname.startsWith(to));

    return (
        <li>
            <Link to={to} className={isActive ? 'active-nav-link' : ''}>
                {children}
            </Link>
        </li>
    );
};

const LoginPage: React.FC<{ onLogin: () => void }> = ({ onLogin }) => (
    <div className="welcome-page" style={{ textAlign: 'center', marginTop: '4rem' }}>
        <h1>Welcome!</h1>
        <br />
        <p>Please log in with Discord to access the panel.</p>
        <br />
        <button onClick={onLogin} className="primary-btn">
            Login with Discord
        </button>
    </div>
);

interface HomePageProps {
    user: User;
}

const HomePage: React.FC<HomePageProps> = ({ user }) => {
    return (
        <section className="section">
            <h1>Home</h1>
            <p style={{ opacity: 0.8, marginBottom: '1rem' }}>
                Welcome to the HeroNetwork Panel, {user.username}</p>
        </section>
    );
};

const App: React.FC = () => {
    const [user, setUser] = useState<User | null>(null);
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState<Stats | null>(null);
    const [boosters, setBoosters] = useState<BoostRecord[]>([]);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        console.log('[PANEL] App mounted, checking for error params');
        const params = new URLSearchParams(window.location.search);
        if (params.get('error') === 'unauthorized') {
            console.error('[PANEL] Unauthorized error detected in URL params');
            setError('You do not have permission to access this panel.');
            setLoading(false);
            window.history.replaceState({}, '', '/');
            return;
        }

        checkAuth();
    }, []);

    const checkAuth = async () => {
        console.log('[PANEL] Checking authentication...');
        try {
            console.log('[PANEL] Making request to:', `${API_URL}/api/auth/user`);
            const response = await axios.get(`${API_URL}/api/auth/user`);
            console.log('[PANEL] Auth check response:', response.data);
            setUser(response.data.user);
            console.log('[PANEL] User authenticated:', response.data.user.username);
            loadStats();
            loadBoosters();
        } catch (err: any) {
            console.error('[PANEL] Auth check failed:', err);
            if (err.response) {
                console.error('[PANEL] Error response status:', err.response.status);
                console.error('[PANEL] Error response data:', err.response.data);
                
                if (err.response.status === 403) {
                    console.error('[PANEL] User not authorized (403)');
                    setError('You do not have permission to access this panel.');
                } else if (err.response.status === 401) {
                    console.log('[PANEL] User not authenticated (401) - this is expected for non-logged in users');
                }
            } else {
                console.error('[PANEL] Network or other error:', err.message);
            }
        } finally {
            setLoading(false);
            console.log('[PANEL] Auth check complete');
        }
    };

    const loadStats = async () => {
        console.log('[PANEL] Loading stats...');
        try {
            const response = await axios.get(`${API_URL}/api/boosts/overall-stats`);
            console.log('[PANEL] Stats loaded:', response.data);
            setStats(response.data);
        } catch (err) {
            console.error('[PANEL] Failed to load stats:', err);
        }
    };

    const loadBoosters = async () => {
        console.log('[PANEL] Loading boosters...');
        try {
            const response = await axios.get(`${API_URL}/api/boosts/boosters`);
            console.log('[PANEL] Boosters loaded, count:', response.data.records?.length || 0);
            setBoosters(response.data.records);
        } catch (err) {
            console.error('[PANEL] Failed to load boosters:', err);
        }
    };

    const handleLogin = () => {
        console.log('[PANEL] Initiating Discord login...');
        const loginUrl = `${API_URL}/api/auth/discord`;
        console.log('[PANEL] Redirecting to:', loginUrl);
        window.location.href = loginUrl;
    };

    const handleLogout = async () => {
        console.log('[PANEL] Logging out...');
        try {
            await axios.get(`${API_URL}/api/auth/logout`);
            console.log('[PANEL] Logout successful');
            setUser(null);
            setStats(null);
            setBoosters([]);
        } catch (err) {
            console.error('[PANEL] Failed to logout:', err);
        }
    };

    if (loading) {
        console.log('[PANEL] Showing loading screen');
        return (
            <div className="app">
                <div className="loading-container">
                    <div className="loading-spinner" />
                    <p className="loading-text">Loading panel...</p>
                </div>
            </div>
        );
    }

    return (
        <Router>
            <div className="app">
                {/* HEADER */}
                <header className="header combined-header">
                    {/* LEFT: NAVIGATION */}
                    <nav className="header-nav">
                        {user && (
                            <ul>
                                <NavLink to="/">Home</NavLink>
                                <NavLink to="/boosts">Boosts</NavLink>
                                <NavLink to="/appeals">Appeals</NavLink>
                                <NavLink to="/birthdays">Birthdays</NavLink>
                                <NavLink to="/settings">Settings</NavLink>
                            </ul>
                        )}
                    </nav>

                    {/* CENTER: LOGO */}
                    <div className="header-logo">
                        <img
                            src="images/MHA-Logo.png"
                            alt="HeroNetwork Logo"
                            className="logo-img"
                            style={{ maxHeight: 60 }}
                        />
                    </div>

                    {/* RIGHT: USER + LOGOUT */}
                    <div className="header-right">
                        {user && (
                            <>
                                <button onClick={handleLogout} className="logout-btn">
                                    Logout
                                </button>
                            </>
                        )}
                    </div>
                </header>

                <main className="content">
                    {error && (
                        <div className="error-message" style={{ maxWidth: 600, margin: '0 auto 1.5rem' }}>
                            {error}
                        </div>
                    )}

                    <Routes>
                        {/* ROOT / HOME */}
                        <Route
                            path="/"
                            element={
                                user ? (
                                    <HomePage user={user} />
                                ) : (
                                    <LoginPage onLogin={handleLogin} />
                                )
                            }
                        />

                        {/* BOOSTS PAGE*/}
                        <Route
                            path="/boosts"
                            element={
                                user ? (
                                    <Boosts stats={stats} boosters={boosters} />
                                ) : (
                                    <Navigate to="/" replace />
                                )
                            }
                        />

                        {/* APPEALS PAGE */}
                        <Route
                            path="/appeals"
                            element={
                                user ? (
                                    <Appeals user={user} />
                                ) : (
                                    <Navigate to="/" replace />
                                )
                            }
                        />

                        {/* BIRTHDAYS PAGE */}
                        <Route
                            path="/birthdays"
                            element={
                                user ? (
                                    <Birthdays />
                                ) : (
                                    <Navigate to="/" replace />
                                )
                            }
                        />

                        {/* SETTINGS PAGE */}
                        <Route
                            path="/settings"
                            element={
                                user ? (
                                    <Settings />
                                ) : (
                                    <Navigate to="/" replace />
                                )
                            }
                        />

                        {/* Catch-all */}
                        <Route path="*" element={<Navigate to="/" replace />} />
                    </Routes>
                </main>

                {/* FOOTER */}
                <footer className="footer">
                    <div className="footer-columns">

                        {/* LEFT COLUMN – LOGO */}
                        <div className="footer-col footer-left">
                            <img
                                src="images/MHA-Logo.png"
                                alt="HeroNetwork Logo"
                                className="footer-logo"
                            />
                        </div>

                        {/* MIDDLE COLUMN - BLANK*/}
                        <div className="footer-col footer-middle">
                            { }
                        </div>

                        {/* RIGHT COLUMN – LINKS */}
                        <div className="footer-col footer-right">
                            <span className="subtitle footer-alt">My Hero Academia Links</span>
                            <ul className="link-list align-right">
                                <li className="link">
                                    <a href="https://discord.gg/myheroacademia" target="_blank">
                                        Discord<i className="bi bi-link-45deg"></i>
                                    </a>
                                </li>
                                <li className="link">
                                    <a href="https://myheroacademia.fandom.com/wiki/My_Hero_Academia_Wiki" target="_blank">
                                        Fandom Wiki<i className="bi bi-link-45deg"></i>
                                    </a>
                                </li>
                                <li className="link">
                                    <a href="https://aminoapps.com/c/my-hero-academia/" target="_blank">
                                        Amino<i className="bi bi-link-45deg"></i>
                                    </a>
                                </li>
                                <li className="link">
                                    <a href="https://www.crunchyroll.com/series/G6NQ5DWZ6/my-hero-academia" target="_blank">
                                        Crunchyroll<i className="bi bi-link-45deg"></i>
                                    </a>
                                </li>
                                <li className="link">
                                    <a href="https://mangaplus.shueisha.co.jp/titles/100017" target="_blank">
                                        Manga<i className="bi bi-link-45deg"></i>
                                    </a>
                                </li>
                                <li className="link">
                                    <a href="https://forms.gle/g7Wpu4hF6dDuTxL86" target="_blank">
                                        DMCA Requests<i className="bi bi-link-45deg"></i>
                                    </a>
                                </li>
                            </ul>
                        </div>

                        {/* COPYRIGHT ROW */}
                        <div className="footer-copy">
                            © {new Date().getFullYear()} HeroNetwork Panel
                        </div>
                    </div>
                </footer>


            </div>
        </Router>
    );
};

export default App;